import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  SERVICE_NAMES,
  TRANSPORT_CONFIG_KEY,
  fetchGateStops,
  fetchStatus,
  type ServiceStatus,
  type TransportService,
} from '../api/transportConfig'
import { Callout } from '../components/Callout'
import { FilterTabs, type FilterTab } from '../components/FilterTabs'
import { StatusPill } from '../components/StatusPill'
import { AreasSection } from './transport/AreasSection'
import { CapacitySection } from './transport/CapacitySection'
import { FaresSection } from './transport/FaresSection'
import { HospitalZonesSection } from './transport/HospitalZonesSection'
import { SectionState } from './transport/SectionState'
import { SettingsSection } from './transport/SettingsSection'
import { retryUnlessForbidden } from './transport/errorMessage'

type Section = 'fares' | 'areas' | 'zones' | 'capacity' | 'settings'

const TABS: readonly FilterTab<Section>[] = [
  { key: 'fares', label: 'Fares' },
  { key: 'areas', label: 'Pickup areas' },
  { key: 'zones', label: 'Hospital zones' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'settings', label: 'Settings' },
]

const ORDER: readonly TransportService[] = [
  'standard_ride',
  'pts',
  'car_hire',
  'executive',
]

/**
 * Transport settings (M38-T07, ADR-0066). amtc_admin only; the server's
 * `transport_config:write` capability is the real guard.
 *
 * Where AMTC enters what Medcor Transport needs before it can sell a trip:
 * fares, pickup areas, hospital zones, daily capacity and the booking settings.
 * Empty means not bookable, so the status strip at the top says, per service,
 * what is still missing. The public sees none of this until go-live (M41).
 */
export function TransportSettingsPage(): ReactNode {
  const { token } = useAuth()
  const [section, setSection] = useState<Section>('fares')
  const status = useQuery({
    queryKey: [...TRANSPORT_CONFIG_KEY, 'status'],
    queryFn: () => fetchStatus(token),
    enabled: Boolean(token),
    retry: retryUnlessForbidden,
  })
  const gateStops = useQuery({
    queryKey: [...TRANSPORT_CONFIG_KEY, 'gate-stops'],
    queryFn: () => fetchGateStops(token),
    enabled: Boolean(token),
    retry: retryUnlessForbidden,
  })

  // A hospital admin who types the address: the server refuses, and the page
  // says whose settings these are rather than offering controls that fail.
  if (status.error instanceof ApiError && status.error.status === 403) {
    return (
      <section className="flex flex-col gap-4">
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Transport settings
        </h1>
        <p className="text-sm text-ink-2">
          Transport settings are managed by AMTC.
        </p>
      </section>
    )
  }

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Transport settings
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Fares, pickup areas, hospital zones, daily capacity and booking
          settings for Medcor Transport.
        </p>
      </div>
      <Callout tone="info">
        Online booking is switched off for the public until go-live. A service
        becomes bookable once all its fares and a daily limit are set.
      </Callout>

      {status.data ? (
        <ul
          aria-label="Which services can be booked"
          className="flex flex-col gap-2"
        >
          {ORDER.map((service) => (
            <StatusLine
              key={service}
              service={service}
              status={status.data[service]}
            />
          ))}
        </ul>
      ) : (
        <SectionState
          isPending={status.isPending}
          error={status.error}
          onRetry={() => void status.refetch()}
          what="which services can be booked"
        />
      )}

      {gateStops.data && (
        <p className="text-sm text-ink-2">
          The safety check stopped {gateStops.data.total}{' '}
          {gateStops.data.total === 1 ? 'booking' : 'bookings'} in the last 30
          days.
        </p>
      )}

      <FilterTabs
        tabs={TABS}
        active={section}
        onChange={setSection}
        label="Transport settings sections"
      />
      {section === 'fares' && <FaresSection />}
      {section === 'areas' && <AreasSection />}
      {section === 'zones' && <HospitalZonesSection />}
      {section === 'capacity' && <CapacitySection />}
      {section === 'settings' && <SettingsSection />}
    </section>
  )
}

function StatusLine({
  service,
  status,
}: {
  service: TransportService
  status: ServiceStatus
}): ReactNode {
  let pill: ReactNode
  let detail = ''
  if (status.phone_only) {
    pill = <StatusPill status="phone" tone="muted" label="Booked by phone" />
  } else if (status.bookable) {
    pill = <StatusPill status="bookable" tone="done" label="Bookable" />
  } else {
    pill = (
      <StatusPill status="not_bookable" tone="attention" label="Not bookable" />
    )
    const missing: string[] = []
    if (status.missing_fares.length > 0) {
      const n = status.missing_fares.length
      missing.push(`${n} fare${n === 1 ? '' : 's'} not set`)
    }
    if (status.missing_capacity) missing.push('no daily limit')
    detail = missing.join(' · ')
  }
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className="min-w-[10rem] font-medium text-ink-1">
        {SERVICE_NAMES[service]}
      </span>
      {pill}
      {detail && <span className="text-ink-3">{detail}</span>}
    </li>
  )
}
